<script lang="ts" module>
  export type MultiSelectOption = { id: number; name: string };
  // A picked value: known options carry their id; typed-in ("add your own") ones don't.
  export type MultiSelectValue = { name: string; id?: number };
</script>

<script lang="ts">
  // Direct subpath import — avoids the package barrel pulling in optional peer deps
  // (markdown/syntax-highlighting) that MultiSelect doesn't need.
  import MultiSelect from "svelte-widgets/MultiSelect.svelte";

  interface Props {
    label: string;
    options: MultiSelectOption[];
    placeholder?: string;
    value?: MultiSelectValue[];
  }

  let {
    label,
    options,
    placeholder = "Search or add your own…",
    value = $bindable([]),
  }: Props = $props();

  const id = $derived(
    "ms-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
  );

  // We drive MultiSelect with plain option names (unique in these lists), so known and
  // typed-in picks share one string array; we map names back to {name, id?} for the form.
  const optionNames = $derived(options.map((o) => o.name));
  let selected = $state<string[]>([]);

  function toValue(name: string): MultiSelectValue {
    const match = options.find((o) => o.name === name);
    return match ? { id: match.id, name: match.name } : { name };
  }

  $effect(() => {
    value = selected.map(toValue);
  });
</script>

<div class="space-y-2">
  <label for={id} class="block text-base font-semibold text-foreground">{label}</label>
  <MultiSelect
    {id}
    options={optionNames}
    bind:value={selected}
    {placeholder}
    allow_user_options="append"
    duplicates="case-insensitive"
    create_option_msg={(s) => `Add “${s.search_text}”`}
    input_class="placeholder:italic"
    --sms-min-height="2.75rem"
    --sms-font-size="1rem"
    --sms-border="1px solid var(--border)"
    --sms-border-radius="0.375rem"
    --sms-bg="var(--background)"
    --sms-text-color="var(--foreground)"
    --sms-focus-border="1px solid var(--ring)"
    --sms-active-color="var(--ring)"
    --sms-placeholder-color="var(--muted-foreground)"
    --sms-options-bg="var(--popover)"
    --sms-options-border="1px solid var(--border)"
    --sms-options-border-radius="0.5rem"
    --sms-li-active-bg="var(--muted)"
    --sms-selected-bg="color-mix(in oklch, var(--primary) 12%, transparent)"
    --sms-selected-text-color="var(--foreground)"
    --sms-remove-btn-hover-bg="color-mix(in oklch, var(--primary) 20%, transparent)"
  />
</div>
